import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import RelationsIcon from "assets/icons/link.svg?react";
import SharedIcon from "assets/icons/share.svg?react";
import DeskIcon from "assets/icons/book.svg?react";
import DeskActiveModal from "components/DeskActiveModal";
import { useContext, useMemo, useRef, useState } from "react";
import CaseRelationsModal from "components/CaseRelationsModal";
import CaseSharesModal from "components/CaseSharesModal";
import { IStatus } from "dto/IStatus";
import StatusBadge from "components/StatusBadge";
import ModalsManagerContext from "contexts/ModalsManager";
import InfoIcon from "assets/icons/info_icon.svg?react";
import CaseIcon from "assets/icons/case.svg?react";
import CaseInfo from "components/CaseInfo";
import classNames from "classnames";
import Popper from "components/Popper";
import { Tooltip } from "@mui/material";
import CasesContext from "contexts/CasesContext";
import deskService from "api/deskService";
import { ItemKind } from "dto/IKindItems";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const initialState = {
  desk: false,
  relations: false,
  shares: false,
};

const CaseStatusIcons: FC<Props> = ({
  Case,
  inList = false,
  searchText,
  showStatus = true,
  keepIconPlace = false,
  inDeskView,
  showGotoCase = false,
  statuses,
  onMetric,
}) => {
  const { t } = useTranslation();
  const { cases: casesContext, setCases: setCasesContext } =
    useContext(CasesContext);

  const deskRef = useRef<HTMLDivElement>();
  const relationsRef = useRef<HTMLDivElement>();
  const sharesRef = useRef<HTMLDivElement>();
  const infoRef = useRef<HTMLDivElement>();
  const [modals, setModals] = useState(initialState);
  const [showMetric, setShowMetric] = useState(false);

  const status = useMemo(
    () =>
      statuses ? statuses.find((s: IStatus) => s.Id === Case?.StatusId) : null,
    [statuses, Case?.StatusId],
  );

  const { modals: bigModals, setModals: setBigModals } =
    useContext(ModalsManagerContext);

  const { mutate: setDesk } = deskService.useSetDesk();

  const navigate = useNavigate();

  const handleNavigateToCase = () => {
    navigate(`/cases/${Case?.Id}/chat`);
  };

  const handleRemoveFromDesk = () => {
    const newCases = [...casesContext];
    const el = newCases.find((p) => p.Id === Case.Id);

    if (el) {
      if (!inDeskView) {
        el.IsDeskActive = !el.IsDeskActive;
        setCasesContext(newCases);
      } else {
        setCasesContext(newCases.filter((x) => x.Id !== Case.Id));
      }
    }

    setDesk(
      {
        Items: [{ Id: Case.Id, Kind: ItemKind.Cases }],
        Show: false,
      },
      {
        onError: () => {},
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      {showGotoCase && (
        <div
          onClick={() => {
            handleNavigateToCase();
          }}
          className={classNames(
            styles.box,
            Case?.SearchHit?.InStatus ? styles.searchHit : "",
          )}
        >
          <Tooltip title={t("cases.goToCase")}>
            <div>
              <CaseIcon className={styles.icon} />
            </div>
          </Tooltip>
        </div>
      )}
      {showStatus && status && (
        <div
          onClick={() => {
            localStorage.setItem("package", JSON.stringify(Case));
            localStorage.setItem("packageType", "CASES");
            setModals(initialState);
            setBigModals({ ...bigModals, editStatus: true });
          }}
          className={classNames(
            styles.box,
            Case?.SearchHit?.InStatus ? styles.searchHit : "",
          )}
        >
          <StatusBadge status={status} />
        </div>
      )}
      {(Case?.IsDeskActive || keepIconPlace) && (
        <div
          ref={deskRef}
          onClick={() => setModals({ ...initialState, desk: true })}
          className={classNames(
            styles.box,
            keepIconPlace && !Case?.IsDeskActive ? styles.hidden : null,
          )}
        >
          <Tooltip title="Odepnij od biurka">
            <div>
              <DeskIcon className={styles.icon} />
            </div>
          </Tooltip>
        </div>
      )}
      {(Case?.RelationsCount > 0 || keepIconPlace) && (
        <div
          ref={relationsRef}
          onClick={() => setModals({ ...initialState, relations: true })}
          className={classNames(
            styles.box,
            keepIconPlace && Case?.RelationsCount === 0 ? styles.hidden : null,
          )}
        >
          <Tooltip title="Relacje">
            <div>
              <RelationsIcon className={styles.icon} />
            </div>
          </Tooltip>
        </div>
      )}
      {(Case?.IsShared || keepIconPlace) && (
        <div
          ref={sharesRef}
          onClick={() => setModals({ ...initialState, shares: true })}
          className={classNames(
            styles.box,
            keepIconPlace && !Case?.IsShared ? styles.hidden : null,
          )}
        >
          <Tooltip title={t("statuses.shared")}>
            <div>
              <SharedIcon className={styles.icon} />
            </div>
          </Tooltip>
        </div>
      )}

      <Tooltip title={t("cases.metric")}>
        <div
          ref={infoRef}
          className={classNames(
            styles.box,
            Case?.SearchHit?.InCourtPortalMetric ? styles.searchHit : "",
          )}
          onClick={() => (onMetric ? onMetric() : setShowMetric(!showMetric))}
        >
          <InfoIcon className={styles.icon} />
        </div>
      </Tooltip>

      <Popper
        open={modals.desk}
        anchorEl={deskRef?.current}
        setOpen={(v) => setModals({ ...initialState, desk: v })}
      >
        <DeskActiveModal
          toggle={() => setModals({ ...initialState, desk: false })}
          onRemove={handleRemoveFromDesk}
        />
      </Popper>

      {Case?.Id && (
        <Popper
          open={modals.relations}
          anchorEl={relationsRef?.current}
          setOpen={(v) => setModals({ ...initialState, relations: v })}
        >
          <CaseRelationsModal
            CaseId={Case.Id}
            toggle={() => setModals({ ...initialState, relations: false })}
          />
        </Popper>
      )}

      {Case?.Id && (
        <Popper
          open={showMetric}
          anchorEl={infoRef?.current}
          setOpen={setShowMetric}
        >
          <CaseInfo
            statuses={statuses}
            tags={[]}
            caseId={Case.Id}
            toggle={() => setShowMetric(false)}
            allowEdit={!inList}
            searchText={searchText}
          />
        </Popper>
      )}

      {Case?.Id && (
        <Popper
          open={modals.shares}
          anchorEl={sharesRef?.current}
          setOpen={(v) => setModals({ ...initialState, shares: v })}
        >
          <CaseSharesModal
            CaseId={Case.Id}
            toggle={() => setModals({ ...initialState, shares: false })}
          />
        </Popper>
      )}
    </div>
  );
};

export default CaseStatusIcons;
